import { IJobIndustry } from 'core/interface/job';
import { IIndustry } from 'core/interface/companies';
import { ILocation } from 'core/interface/work-experience-side';

type IndustryLike =
  | number
  | IJobIndustry
  | { id?: number; name?: string; title?: string }
  | null
  | undefined;

type LocationLike =
  | number
  | ILocation
  | { id?: number; display_name?: string; name?: string }
  | null
  | undefined;

type LocationCatalogItem = { id: number; display_name: string };

export const getJobIndustryIds = (
  industries?: IndustryLike[] | null
): number[] => {
  if (!industries?.length) {
    return [];
  }

  return industries
    .map((item) => {
      if (typeof item === 'number') {
        return item;
      }
      if (item && typeof item === 'object' && item.id != null) {
        return Number(item.id);
      }
      return null;
    })
    .filter((id): id is number => id != null && Number.isFinite(id));
};

export const normalizeJobIndustriesFromApi = (
  industries?: IndustryLike[] | null,
  catalog?: IIndustry[]
): IJobIndustry[] => {
  if (!industries?.length) {
    return [];
  }

  return industries
    .map((item) => {
      if (typeof item === 'object' && item && item.id != null) {
        const name =
          item.name ||
          ('title' in item ? item.title : undefined) ||
          catalog?.find((entry) => entry.id === item.id)?.name ||
          '';
        return name ? { id: Number(item.id), name: String(name) } : null;
      }

      if (typeof item === 'number') {
        const found = catalog?.find((entry) => entry.id === item);
        return found ? { id: found.id, name: found.name } : null;
      }

      return null;
    })
    .filter((item): item is IJobIndustry => Boolean(item?.id && item.name));
};

export const getJobIndustryDisplayNames = (
  industries?: IndustryLike[] | null,
  catalog?: IIndustry[]
): string[] => {
  const normalized = normalizeJobIndustriesFromApi(industries, catalog);
  if (normalized.length) {
    return normalized.map((item) => item.name);
  }

  return getJobIndustryIds(industries)
    .map((id) => catalog?.find((entry) => entry.id === id)?.name || '')
    .filter(Boolean);
};

export const jobIndustriesToApiPayload = (
  selectedIds: number[],
  catalog: IIndustry[],
  existing?: IndustryLike[] | null
): IJobIndustry[] => {
  const nameById = new Map<number, string>();

  normalizeJobIndustriesFromApi(existing, catalog).forEach((item) => {
    nameById.set(item.id, item.name);
  });
  catalog.forEach((item) => {
    if (!nameById.has(item.id)) {
      nameById.set(item.id, item.name);
    }
  });

  return selectedIds
    .map((id) => {
      const name = nameById.get(id);
      return name ? { id, name } : null;
    })
    .filter((item): item is IJobIndustry => Boolean(item?.id && item.name));
};

export const industriesInputToApiPayload = (
  industries: IndustryLike[] | null | undefined,
  catalog: IIndustry[]
): IJobIndustry[] => {
  if (!industries?.length) {
    return [];
  }

  return industries
    .map((item) => {
      if (typeof item === 'object' && item && item.id != null) {
        const name =
          item.name ||
          ('title' in item ? item.title : undefined) ||
          catalog.find((entry) => entry.id === item.id)?.name ||
          '';
        return name ? { id: Number(item.id), name: String(name) } : null;
      }

      if (typeof item === 'number') {
        const found = catalog.find((entry) => entry.id === item);
        return found ? { id: found.id, name: found.name } : null;
      }

      return null;
    })
    .filter((item): item is IJobIndustry => Boolean(item?.id && item.name));
};

export const getJobLocationIds = (
  locations?: LocationLike[] | null
): number[] => {
  if (!locations?.length) {
    return [];
  }

  return locations
    .map((item) => {
      if (typeof item === 'number') {
        return item;
      }
      if (item && typeof item === 'object' && item.id != null) {
        return Number(item.id);
      }
      return null;
    })
    .filter((id): id is number => id != null && Number.isFinite(id));
};

export const normalizeJobLocationsFromApi = (
  locations?: LocationLike[] | null,
  catalog?: LocationCatalogItem[]
): ILocation[] => {
  if (!locations?.length) {
    return [];
  }

  return locations
    .map((item) => {
      if (typeof item === 'object' && item && item.id != null) {
        const displayName =
          item.display_name ||
          ('name' in item ? item.name : undefined) ||
          catalog?.find((entry) => entry.id === item.id)?.display_name ||
          '';
        return displayName
          ? { id: Number(item.id), display_name: String(displayName) }
          : null;
      }

      if (typeof item === 'number') {
        const found = catalog?.find((entry) => entry.id === item);
        return found
          ? { id: found.id, display_name: found.display_name }
          : null;
      }

      return null;
    })
    .filter(
      (item): item is ILocation => Boolean(item?.id && item.display_name)
    );
};

export const getJobLocationDisplayNames = (
  locations?: LocationLike[] | null,
  catalog?: LocationCatalogItem[]
): string[] => {
  const normalized = normalizeJobLocationsFromApi(locations, catalog);
  if (normalized.length) {
    return normalized.map((item) => item.display_name);
  }

  return getJobLocationIds(locations)
    .map((id) => catalog?.find((entry) => entry.id === id)?.display_name || '')
    .filter(Boolean);
};

export const jobLocationsToApiPayload = (
  locations: ILocation[]
): ILocation[] =>
  locations
    .filter((item) => item?.id)
    .map((item) => ({
      id: item.id,
      display_name: item.display_name || ''
    }));
